import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, del, get, patch, post, postForm, withQuery } from "./client";
import { SITES_KEY, siteQuery, type SiteFilterQuery } from "./sites";

/** Routing: the planner (against the JSON the planning page always used under
 * `/routing/...`), and `/api/v1/routing` for the planner's context, the week
 * grid, saved plans, their lifecycle and the day sheets. */

export interface PlannerContext {
  filters: {
    statuses: { value: string; label: string }[];
    contracts: { id: number; name: string; code: string }[];
    regions: { id: number; name: string; contract_id: number }[];
    clusters: { id: number; name: string; region_id: number | null; contract_id: number }[];
    providers: { id: number; name: string }[];
  };
  default_provider_id: number | null;
  meeting_points: { id: number; contract_id: number; name: string; address: string | null; lat: number; lon: number }[];
  start: { label: string; lat: number | null; lon: number | null };
  selection: { site_ids: number[]; chips: { field: string; label: string; value: string; remove_query: string }[] };
  filter_query: string;
  /** Sites whose last planned visit was not done and that are still owed one. */
  visit_not_done: number;
}

export interface Point {
  lat: number;
  lon: number;
  label: string;
}

export interface PlanFilter {
  status: string[];
  contract_id: string | null;
  cluster_id: string | null;
  provider_id: string | null;
  valve_make: string | null;
  dn_min: string | null;
  dn_max: string | null;
  assigned: string | null;
  flagged: string | null;
  red_list: string | null;
  has_qcp: string | null;
  no_qcp: string | null;
  located: string | null;
  not_located: string | null;
  q: string | null;
  region_id: string | null;
  deferred: string | null;
  site_type: string | null;
  not_done: string | null;
  work_order_type: string | null;
}

export interface PlanOptions {
  day_start: string;
  day_end: string;
  on_site_minutes: number;
  max_per_day: number | null;
  strategy: string;
  return_home: boolean;
  first_day: string | null;
  include_weekends: boolean;
}

export const DEFAULT_OPTIONS: PlanOptions = {
  day_start: "07:00",
  day_end: "16:00",
  on_site_minutes: 90,
  max_per_day: null,
  strategy: "cluster",
  return_home: true,
  first_day: null,
  include_weekends: false,
};

export interface PlannedStop {
  seq: number;
  site_id: number;
  name: string;
  lat: number;
  lon: number;
  leg_m: number;
  leg_s: number;
  arrive_min: number;
  depart_min: number;
  flagged: boolean;
}

export interface PlannedDay {
  day_index: number;
  date: string | null;
  day_type: string | null;
  total_m: number;
  total_s: number;
  stops: PlannedStop[];
}

/** A PM42 job the scheduling gate leaves out of a plan, and why. `basis`
 * "default" means nobody has stated its work order type. */
export interface GatedSite {
  site_id: number;
  name: string;
  reasons: string[];
  basis: "default" | "work_order" | "inspection";
}

export interface Proposal {
  origin: Point;
  meeting: Point | null;
  approximate: boolean;
  on_site_minutes: number;
  return_home: boolean;
  skipped_site_ids: number[];
  left_out?: LeftOut;
  /** Left out by the scheduling gate; empty while it is off. */
  gated?: GatedSite[];
  days: PlannedDay[];
}

/** The site filter as the planner posts it: the same raw strings the site
 * list uses, so a partial payload widens the same way -- and all of it, since
 * the count reads the whole query string and the plan must take the same sites. */
export function planFilter(filter: SiteFilterQuery): PlanFilter {
  const or = (v: string) => (v === "" ? null : v);
  return {
    status: filter.status,
    contract_id: or(filter.contract_id),
    cluster_id: or(filter.cluster_id),
    provider_id: or(filter.provider_id),
    valve_make: or(filter.valve_make),
    dn_min: or(filter.dn_min),
    dn_max: or(filter.dn_max),
    assigned: or(filter.assigned),
    flagged: or(filter.flagged),
    red_list: or(filter.red_list),
    has_qcp: or(filter.has_qcp),
    no_qcp: or(filter.no_qcp),
    located: or(filter.located),
    not_located: or(filter.not_located),
    q: or(filter.q),
    region_id: or(filter.region_id),
    deferred: or(filter.deferred),
    site_type: or(filter.site_type),
    not_done: or(filter.not_done),
    work_order_type: or(filter.work_order_type),
  };
}

export function planBody(
  origin: Point,
  meeting: Point | null,
  siteIds: number[],
  filter: SiteFilterQuery,
  options: PlanOptions,
  includePlanned = false,
) {
  return {
    origin: { text: origin.label, lat: origin.lat, lon: origin.lon },
    meeting: meeting ? { text: meeting.label, lat: meeting.lat, lon: meeting.lon } : null,
    site_ids: siteIds,
    include_planned: includePlanned,
    filt: planFilter(filter),
    ...options,
    on_site_minutes: options.on_site_minutes || 0,
    first_day: options.first_day || null,
  };
}

/** What gets saved is the plan the server returned, not the controls: the
 * point the route was built around and its days as proposed. */
export function saveBody(proposal: Proposal, contractId: number, name: string, filter: SiteFilterQuery, options: PlanOptions) {
  return {
    contract_id: contractId,
    name: name.trim() || "Route plan",
    params: {
      origin: proposal.origin,
      meeting: proposal.meeting,
      on_site_minutes: proposal.on_site_minutes,
      return_home: proposal.return_home,
      strategy: options.strategy,
      day_start: options.day_start,
      day_end: options.day_end,
      filt: planFilter(filter),
    },
    days: proposal.days.map((d) => ({
      day_index: d.day_index,
      total_m: d.total_m,
      total_s: d.total_s,
      stops: d.stops.map(({ seq, site_id, leg_m, leg_s, arrive_min, depart_min }) => ({ seq, site_id, leg_m, leg_s, arrive_min, depart_min })),
    })),
  };
}

/** The journey a day draws: start, meeting point if any, the stops, and home
 * again when the day returns. Must match the server's `day_points`. */
export function dayPoints(proposal: Proposal, day: PlannedDay): [number, number][] {
  const points: [number, number][] = [[proposal.origin.lat, proposal.origin.lon]];
  if (proposal.meeting) {
    points.push([proposal.meeting.lat, proposal.meeting.lon]);
  }
  for (const stop of day.stops) {
    points.push([stop.lat, stop.lon]);
  }
  if (proposal.return_home) {
    points.push([proposal.origin.lat, proposal.origin.lon]);
  }
  return points;
}

export const DAY_COLORS = ["#00655B", "#B45309", "#1D4ED8", "#9D174D", "#4D7C0F", "#7C3AED", "#0E7490", "#B91C1C"];

export function dayColor(index: number): string {
  return DAY_COLORS[index % DAY_COLORS.length];
}

export function fmtKm(metres: number): string {
  return (metres / 1000).toFixed(1);
}

export function fmtDuration(seconds: number): string {
  const minutes = Math.round(seconds / 60);
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${String(m).padStart(2, "0")}m` : `${m}m`;
}

export function clock(minutes: number): string {
  const total = Math.round(minutes);
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

export function usePlannerContext(search: string) {
  return useQuery<PlannerContext, ApiError>({
    queryKey: ["routing", "context", search],
    queryFn: () => get<PlannerContext>(`/api/v1/routing/context${search}`),
  });
}

/** What the planner would take with this filter: how many sites, and how many
 * of those have coordinates and so can be routed. */
export interface PlannerCount {
  sites: number;
  located: number;
  /** Left out because already on a live route plan, or visited; none when
   * the planner asked to include them. */
  left_out?: LeftOut;
  /** What the scheduling gate leaves out: how many, and the first fifty. */
  gated?: { count: number; sites: GatedSite[] };
}

export interface LeftOut {
  planned: number;
  visited: number;
  /** The live plans holding the planned ones, by name. */
  plans: string[];
}

/** The count's address: the site list's own query string, plus the ticked
 * selection the planner was opened with. */
export function countQuery(filter: SiteFilterQuery, siteIds: readonly number[], includePlanned: boolean): string {
  const url = siteQuery(filter, "/api/v1/routing/count");
  const extra = [...siteIds.map((id) => `site_ids=${id}`), ...(includePlanned ? ["include_planned=true"] : [])].join("&");
  if (extra === "") {
    return url;
  }
  return url.includes("?") ? `${url}&${extra}` : `${url}?${extra}`;
}

export function usePlannerCount(filter: SiteFilterQuery, siteIds: readonly number[], includePlanned: boolean) {
  return useQuery<PlannerCount, ApiError>({
    queryKey: ["routing", "count", filter, siteIds, includePlanned],
    queryFn: () => get<PlannerCount>(countQuery(filter, siteIds, includePlanned)),
    placeholderData: (previous) => previous,
  });
}

export function useGeocode() {
  return useMutation<{ lat: number | null; lon: number | null }, ApiError, string>({
    mutationFn: (text) => get(withQuery("/routing/api/geocode", { text })),
  });
}

export async function directions(points: [number, number][]): Promise<[number, number][] | null> {
  try {
    const body = await post<{ points: [number, number][] | null }>("/routing/api/directions", { points });
    return body.points && body.points.length > 1 ? body.points : null;
  } catch {
    return null;
  }
}

export function usePlanRoutes() {
  return useMutation<Proposal, ApiError, ReturnType<typeof planBody>>({
    mutationFn: (body) => post<Proposal>("/routing/plan", body),
  });
}

export function useSavePlan() {
  const client = useQueryClient();
  return useMutation<{ plan_id: number; days: number; stops: number }, ApiError, ReturnType<typeof saveBody>>({
    mutationFn: (body) => post("/routing/save", body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["routing", "plans"] });
      void client.invalidateQueries({ queryKey: ["routing", "schedule"] });
    },
  });
}

export function useAssignDay() {
  const client = useQueryClient();
  return useMutation<{ day_id: number; provider_id: number; date: string }, ApiError, { planId: number; dayIndex: number; provider_id: number; date: string }>({
    mutationFn: ({ planId, dayIndex, ...body }) => post(`/routing/${planId}/days/${dayIndex}/assign`, body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["routing"] });
    },
  });
}

export interface Accepted {
  plan_id: number;
  accepted_at: string;
  inspections_created: number;
  inspections_existing: number;
  stops: number;
}

export function useAcceptPlan() {
  const client = useQueryClient();
  return useMutation<Accepted, ApiError, number>({
    mutationFn: (planId) => post<Accepted>(`/routing/${planId}/accept`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["routing"] });
      void client.invalidateQueries({ queryKey: ["inspections"] });
    },
  });
}

// --- the schedule ----------------------------------------------------------------

export interface Schedule {
  monday: string;
  sunday: string;
  prev_week: string;
  next_week: string;
  columns: {
    date: string;
    is_today: boolean;
    is_weekend: boolean;
    cards: {
      plan_id: number;
      plan_name: string;
      day_index: number;
      provider_name: string;
      stops: number;
      /** The day's stops by what became of them. */
      done: number;
      not_done: number;
      pending: number;
      completed: boolean;
    }[];
  }[];
  has_days: boolean;
}

export function useSchedule(week: string | null) {
  return useQuery<Schedule, ApiError>({
    queryKey: ["routing", "schedule", week],
    queryFn: () => get<Schedule>(withQuery("/api/v1/routing/schedule", { week })),
  });
}

// --- saved plans -----------------------------------------------------------------

export interface Progress {
  days: number;
  days_complete: number;
  stops: number;
  done: number;
  not_done: number;
  pending: number;
}

export interface PlanHead {
  id: number;
  name: string;
  contract_id: number;
  contract: string;
  state: "proposal" | "accepted" | "completed" | "archived";
  created_at: string | null;
  accepted_at: string | null;
  completed_at: string | null;
  archived_at: string | null;
  editable: boolean;
  params: Record<string, unknown>;
}

/** What the crew recorded at a stop (the visit header), beside the plan. */
export interface StopActualView {
  arrived_at: string | null;
  departed_at: string | null;
  arrive_clock: string | null;
  depart_clock: string | null;
  /** Negative is early. */
  late_minutes: number | null;
  inspection_id: number;
  kind: "pm41" | "pm42";
}

/** The other half of a priority swap. */
export interface SwapRef {
  stop_id: number;
  site_name: string | null;
}

export interface PlanStop {
  id: number;
  seq: number;
  site: {
    id: number;
    name: string;
    status: string;
    status_label: string;
    address: string | null;
    /** The works order: the City's notification number. */
    notif_no: string | null;
    rs_no: string | null;
    cluster_id?: number;
  } | null;
  assigned: { id: number; name: string } | null;
  arrive_min: number;
  depart_min: number;
  actual?: StopActualView | null;
  leg_m: number;
  leg_s: number;
  outcome: "pending" | "done" | "not_done";
  outcome_note: string | null;
  /** Why it was not done, from the reasons list; null before the list. */
  outcome_reason: string | null;
  outcome_reason_label: string | null;
  outcome_at: string | null;
  /** On a stop not done: the visit that repeats it, once the site is planned again. */
  replaced_by: VisitRef | null;
  /** On that visit: the missed one it repeats, and why that was missed. */
  replaces: VisitRef | null;
  /** A priority swap: the stop this one took the place of. */
  swapped_in_for?: SwapRef | null;
  /** A priority swap: the stop that took this one's place. */
  swapped_out_for?: SwapRef | null;
  /** The completed visit sheets filed for this stop, kept on its site. */
  documents: StopDocument[];
}

export interface StopDocument {
  id: number;
  kind: string;
  label: string;
  filename: string;
  url: string;
}

/** Another planned visit to the same site. */
export interface VisitRef {
  stop_id: number;
  plan_id: number;
  plan_name: string;
  day_index: number;
  date: string | null;
  note: string | null;
}

export interface PlanDay {
  day_index: number;
  date: string | null;
  day_type: string | null;
  provider: { id: number; name: string } | null;
  total_m: number;
  total_s: number;
  completed_at: string | null;
  geometry: number[][] | null;
  stops: PlanStop[];
}

export interface PlanDetail {
  plan: PlanHead;
  progress: Progress;
  days: PlanDay[];
  providers: { id: number; name: string }[];
  outcomes: string[];
  /** Why a visit can be not done: the list the crew defer a site with. */
  reasons: Reason[];
  urls: { daysheets: string; daysheet_pdf: string };
}

export const planKey = (id: number) => ["routing", "plans", id] as const;

export type PlanRow = PlanHead & { progress: Progress };

/** Plans still waiting to be accepted: the count on the Plans tab. */
export function awaitingAcceptance(rows: readonly PlanRow[]): number {
  return rows.filter((row) => row.state === "proposal").length;
}

export function usePlans(archived: boolean) {
  return useQuery<{ rows: PlanRow[]; archived: boolean }, ApiError>({
    queryKey: ["routing", "plans", { archived }],
    queryFn: () => get(withQuery("/api/v1/routing/plans", { archived: archived ? "true" : null })),
  });
}

export function usePlan(id: number | null) {
  return useQuery<PlanDetail, ApiError>({
    queryKey: planKey(id ?? 0),
    queryFn: () => get<PlanDetail>(`/api/v1/routing/plans/${id}`),
    enabled: id !== null,
  });
}

function usePlanWrite<TVars>(id: number, write: (vars: TVars) => Promise<PlanDetail>, also: readonly unknown[][] = []) {
  const client = useQueryClient();
  return useMutation<PlanDetail, ApiError, TVars>({
    mutationFn: write,
    onSuccess: (detail) => {
      client.setQueryData(planKey(id), detail);
      void client.invalidateQueries({ queryKey: ["routing", "plans"], exact: false });
      void client.invalidateQueries({ queryKey: ["routing", "schedule"] });
      for (const other of also) {
        void client.invalidateQueries({ queryKey: other });
      }
    },
  });
}

export function useArchivePlan(id: number) {
  return usePlanWrite(id, (restore: boolean) => post<PlanDetail>(`/api/v1/routing/plans/${id}/${restore ? "restore" : "archive"}`));
}

/** Delete an archived plan for good. The plan lists refresh; the plan itself
 * is gone, so it is dropped rather than refreshed. */
export function useDeletePlan(id: number) {
  const client = useQueryClient();
  return useMutation<{ deleted: number; name: string }, ApiError, void>({
    mutationFn: () => del(`/api/v1/routing/plans/${id}`),
    onSuccess: () => {
      client.removeQueries({ queryKey: planKey(id) });
      void client.invalidateQueries({ queryKey: ["routing", "plans"], exact: false });
      void client.invalidateQueries({ queryKey: ["routing", "count"] });
      void client.invalidateQueries({ queryKey: ["routing", "report"] });
    },
  });
}

/** Rename a plan. The name is a label: the id is what everything links by. */
export function useRenamePlan(id: number) {
  return usePlanWrite(id, (name: string) => patch<PlanDetail>(`/api/v1/routing/plans/${id}`, { name }));
}

export function useCompletePlan(id: number) {
  return usePlanWrite(id, (reopen: boolean) => post<PlanDetail>(`/api/v1/routing/plans/${id}/complete`, { reopen }));
}

export function useCompleteDay(id: number) {
  return usePlanWrite(id, (vars: { dayIndex: number; reopen: boolean }) =>
    post<PlanDetail>(`/api/v1/routing/plans/${id}/days/${vars.dayIndex}/complete`, { reopen: vars.reopen }),
  );
}

export function useReassignDay(id: number) {
  return usePlanWrite(id, (vars: { dayIndex: number; provider_id: number }) =>
    post<PlanDetail>(`/api/v1/routing/plans/${id}/days/${vars.dayIndex}/crew`, { provider_id: vars.provider_id }),
  );
}

export function useStopOutcome(id: number) {
  return usePlanWrite(id, (vars: { stopId: number; outcome: string; note: string | null; reason?: string | null; reassign_to: number | null }) =>
    post<PlanDetail>(`/api/v1/routing/plans/${id}/stops/${vars.stopId}/outcome`, {
      outcome: vars.outcome,
      note: vars.note,
      reason: vars.reason ?? null,
      reassign_to: vars.reassign_to,
    }),
  );
}

/** A priority swap: the stop's slot goes to another site in its cluster that
 * passes the scheduling gate. */
/** A swap owes the swapped-out site a visit again (the planner's count), moves
 * the incoming site to the day's crew (sites) and, on an accepted plan, cancels
 * one draft and opens another (inspections): all three are marked stale. */
export function useSwapStop(id: number) {
  return usePlanWrite(
    id,
    (vars: { stopId: number; site_id: number; reason: string }) =>
      post<PlanDetail>(`/api/v1/routing/plans/${id}/stops/${vars.stopId}/swap`, { site_id: vars.site_id, reason: vars.reason }),
    [["routing", "count"], [...SITES_KEY], ["inspections"]],
  );
}

/** File a stop's completed visit sheet -- a PDF, or a photo or scan of it. It
 * is kept on the stop's site, so the site's documents refresh as well. */
export function useUploadVisitSheet(id: number) {
  const client = useQueryClient();
  return useMutation<PlanDetail, ApiError, { stopId: number; file: File }>({
    mutationFn: (vars) => {
      const form = new FormData();
      form.append("file", vars.file, vars.file.name);
      return postForm<PlanDetail>(`/api/v1/routing/plans/${id}/stops/${vars.stopId}/visit-sheet`, form);
    },
    onSuccess: (detail) => {
      client.setQueryData(planKey(id), detail);
      void client.invalidateQueries({ queryKey: SITES_KEY });
    },
  });
}

// --- the visits report ------------------------------------------------------------

/** One reason a visit could not be done. */
export interface Reason {
  code: string;
  label: string;
}

export interface ReportTally {
  planned: number;
  done: number;
  not_done: number;
  pending: number;
  /** Pending, and past their day: nobody has said what happened. */
  overdue: number;
  sheets: number;
  /** Done as a whole percentage of planned; null when nothing was planned. */
  completion: number | null;
}

export interface ReportRow {
  stop_id: number;
  plan_id: number;
  plan_name: string;
  day_index: number;
  date: string;
  crew_id: number;
  crew: string;
  seq: number;
  arrive_min: number;
  site_id: number;
  site_name: string;
  work_order: string | null;
  cluster: string | null;
  region: string | null;
  outcome: "pending" | "done" | "not_done";
  note: string | null;
  /** Why it was not done, from the reasons list; null before the list. */
  reason: string | null;
  reason_label: string | null;
  recorded_at: string | null;
  sheets: number;
  planned_again: string | null;
  overdue: boolean;
  /** The site's position, when it has one. */
  lat: number | null;
  lon: number | null;
  /** How far the site's work has got, and whether its Scoping QCP is on file. */
  site_status: string;
  scoped: boolean;
}

export interface VisitReport {
  period: { kind: string; start: string; end: string; label: string; prev_on: string | null; next_on: string | null };
  filters: { provider_id: number | null; outcome: string | null; plan_id: number | null; region_id: number | null; q: string | null; text: string };
  today: string;
  options: {
    crews: { id: number; name: string }[];
    plans: { id: number; name: string }[];
    regions: { id: number; name: string }[];
    outcomes: { value: string; label: string }[];
    reasons: Reason[];
  };
  total: ReportTally;
  by_crew: (ReportTally & { crew: string })[];
  by_day: (ReportTally & { date: string })[];
  rows: ReportRow[];
  /** The downloads of this same view; `dashboard_pdf` is the dashboard's, charts first. */
  urls: { xlsx: string; pdf: string; dashboard_pdf: string };
}

/** The report the address names. The previous one stays on screen while the
 * next loads, so stepping a week never flashes an empty page. */
export function useVisitReport(url: string) {
  return useQuery<VisitReport, ApiError>({
    queryKey: ["routing", "report", url],
    queryFn: () => get<VisitReport>(url),
    placeholderData: keepPreviousData,
  });
}

// --- day sheets ----------------------------------------------------------------

export interface SheetStop {
  seq: number;
  site_id: number;
  name: string;
  valve: string | null;
  rs_no: string | null;
  func_loc: string | null;
  equip_no: string | null;
  notif_no: string | null;
  site_type: string | null;
  age: string | null;
  status: string | null;
  address: string | null;
  coord_source: string;
  has_qcp: boolean;
  leg_km: string;
  leg_duration: string;
  arrive_clock: string;
  depart_clock: string;
  lat: number | null;
  lon: number | null;
  flagged: boolean;
  flag_summary: string | null;
  actual_arrive_clock?: string | null;
  actual_depart_clock?: string | null;
}

export interface DaySheet {
  day_index: number;
  day_number: number;
  total_days: number;
  date_label: string | null;
  color: string;
  contract_label: string;
  origin_label: string;
  stops: SheetStop[];
  total_sites: number;
  qcp_count: number;
  total_km: string;
  drive_time: string;
  depart_clock: string;
  return_clock: string;
  returns_home: boolean;
  check_on_site: string[];
  map_data: { line: [number, number][]; markers: { lat: number; lon: number; label: string }[]; color: string; approximate_line: boolean };
  approximate_line: boolean;
  has_actuals?: boolean;
  pdf_url: string;
}

export function useDaySheets(planId: number | null, day: number | null) {
  return useQuery<{ plan: { id: number; name: string }; sheets: DaySheet[] }, ApiError>({
    queryKey: ["routing", "plans", planId, "daysheets", day],
    queryFn: () => get(withQuery(`/api/v1/routing/plans/${planId}/daysheets`, { day })),
    enabled: planId !== null,
  });
}

// --- site progress ----------------------------------------------------------------

export interface ProgressStage {
  status: string;
  label: string;
  /** A CSS colour, as the main dashboard's map draws the stage. */
  color: string;
  count: number;
}

export interface ProgressSite {
  id: number;
  name: string;
  work_order: string | null;
  status: string;
  scoped: boolean;
  region: string | null;
  lat: number;
  lon: number;
}

/** `/api/v1/routing/progress`: every site by stage, how many are scoped, by
 * region, and the ones with a position. Not tied to a period. */
export interface SiteProgress {
  total: number;
  scoped: number;
  located: number;
  stages: ProgressStage[];
  by_region: { region_id: number | null; region: string; total: number; scoped: number; counts: Record<string, number> }[];
  sites: ProgressSite[];
  options: { regions: { id: number; name: string }[] };
}

export function useSiteProgress(regionId: string) {
  return useQuery<SiteProgress, ApiError>({
    queryKey: ["routing", "progress", regionId],
    queryFn: () => get<SiteProgress>(withQuery("/api/v1/routing/progress", { region_id: regionId })),
    placeholderData: keepPreviousData,
  });
}
